import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { FiPlus, FiTrash2, FiSearch } from 'react-icons/fi';

export default function CreateBill() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState({ vatPercent: 0, currency: 'SAR' });
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCustomerDrop, setShowCustomerDrop] = useState(false);

  const [bill, setBill] = useState({
    billNumber: '', customerName: '', customerPhone: '',
    items: [{ description: '', itemType: 'stitching', quantity: 1, price: 0, total: 0 }],
    vatPercent: 0, advancePaid: 0, notes: ''
  });

  useEffect(() => {
    api.get('/products').then(r => setProducts(r.data.products));
    api.get('/settings').then(r => {
      const s = r.data.settings;
      setSettings(s);
      setBill(prev => ({ ...prev, vatPercent: s.vatPercent || 0,
        billNumber: s.billNumberFormat === 'auto' ? 'auto' : '' }));
    }).catch(() => {});
  }, []);

  const searchCustomers = async (q) => {
    if (!q) { setCustomers([]); return; }
    const r = await api.get('/customers', { params: { search: q, limit: 5 } });
    setCustomers(r.data.customers);
    setShowCustomerDrop(true);
  };

  const selectCustomer = (c) => {
    setSelectedCustomer(c);
    setBill(prev => ({
      ...prev,
      customer: c._id,
      customerName: c.name,
      customerPhone: c.phone
    }));
    setCustomerSearch(c.name);
    setShowCustomerDrop(false);
    // Pre-fill stitching item if they have measurements
    if (c.measurements?.[0]?.price) {
      const m = c.measurements[0];
      setBill(prev => ({
        ...prev,
        customer: c._id,
        customerName: c.name,
        customerPhone: c.phone,
        advancePaid: m.advance || 0,
        items: [{
          description: `Stitching - ${m.type || 'Suit'} x${m.suitQuantity || 1}`,
          itemType: 'stitching',
          quantity: m.suitQuantity || 1,
          price: m.price || 0,
          total: m.price || 0
        }]
      }));
    }
  };

  const updateItem = (idx, field, val) => {
    setBill(prev => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: val };
      // ── Stitching items: `price` is already the staff-entered total for
      // the whole order (however many suits `quantity` says), not a
      // per-unit rate — only real per-unit product lines multiply by qty. ──
      if (field === 'quantity' || field === 'price' || field === 'itemType') {
        items[idx].total = items[idx].itemType === 'product'
          ? (parseFloat(items[idx].quantity) || 0) * (parseFloat(items[idx].price) || 0)
          : (parseFloat(items[idx].price) || 0);
      }
      if (field === 'productId') {
        const p = products.find(p => p._id === val);
        if (p) { items[idx].description = p.name; items[idx].price = p.price; items[idx].total = p.price * items[idx].quantity; }
      }
      return { ...prev, items };
    });
  };

  const addItem = () => setBill(prev => ({
    ...prev,
    items: [...prev.items, { description: '', itemType: 'stitching', quantity: 1, price: 0, total: 0 }]
  }));

  const removeItem = (idx) => setBill(prev => ({
    ...prev,
    items: prev.items.filter((_, i) => i !== idx)
  }));

  const subtotal = bill.items.reduce((s, i) => s + (i.total || 0), 0);
  const vatAmt = (subtotal * (parseFloat(bill.vatPercent) || 0)) / 100;
  const total = subtotal + vatAmt;
  const remaining = total - (parseFloat(bill.advancePaid) || 0);

  const handleSubmit = async () => {
    if (!bill.customerName) return toast.error('Select a customer');
    if (bill.items.some(i => !i.description)) return toast.error('Fill all item descriptions');
    setLoading(true);
    try {
      const payload = { ...bill, customer: selectedCustomer?._id };
      const res = await api.post('/bills', payload);
      toast.success('Bill created! Email sent to customer.');
      navigate(`/bills/${res.data.bill._id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create bill');
    }
    setLoading(false);
  };

  return (
    <div>
      <div className="card" style={{ marginBottom:16 }}>
        <div className="card-header"><h3 className="card-title">📋 Bill Details</h3></div>
        <div className="card-body">
          <div className="form-row-2">
            <div className="form-group">
              <label className="form-label">Bill Number</label>
              <input className="form-control"
                placeholder={settings.billNumberFormat === 'auto' ? 'Auto-generated' : 'Enter bill number'}
                value={bill.billNumber === 'auto' ? '' : bill.billNumber}
                onChange={e => setBill({...bill, billNumber: e.target.value})}
                disabled={settings.billNumberFormat === 'auto'}
              />
              {settings.billNumberFormat === 'auto' && <small style={{ color:'#94a3b8' }}>Auto-incremented</small>}
            </div>
            <div className="form-group">
              <label className="form-label">Customer <span className="required">*</span></label>
              <div style={{ position:'relative' }}>
                <FiSearch size={14} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'#94a3b8' }} />
                <input className="form-control" style={{ paddingLeft:32 }}
                  placeholder="Search customer..." value={customerSearch}
                  onChange={e => { setCustomerSearch(e.target.value); searchCustomers(e.target.value); }}
                  onFocus={() => customerSearch && setShowCustomerDrop(true)}
                />
                {showCustomerDrop && customers.length > 0 && (
                  <div style={{ position:'absolute', top:'100%', left:0, right:0, background:'white', border:'1px solid #e2e8f0', borderRadius:8, boxShadow:'0 4px 12px rgba(0,0,0,0.1)', zIndex:100 }}>
                    {customers.map(c => (
                      <div key={c._id} style={{ padding:'10px 14px', cursor:'pointer', borderBottom:'1px solid #f1f5f9' }}
                        onMouseDown={() => selectCustomer(c)}>
                        <div style={{ fontWeight:600 }}>{c.name}</div>
                        <div style={{ fontSize:12, color:'#94a3b8' }}>{c.phone}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {bill.customerName && !showCustomerDrop && (
                <div className="form-row-2" style={{ marginTop:8 }}>
                  <input className="form-control" value={bill.customerName}
                    onChange={e => setBill({...bill, customerName: e.target.value})} placeholder="Customer name" />
                  <input className="form-control" value={bill.customerPhone}
                    onChange={e => setBill({...bill, customerPhone: e.target.value})} placeholder="Phone" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Items */}
      <div className="card" style={{ marginBottom:16 }}>
        <div className="card-header">
          <h3 className="card-title">📦 Bill Items</h3>
          <button className="btn btn-outline btn-sm" onClick={addItem}><FiPlus size={13} /> Add Item</button>
        </div>
        <div className="card-body">
          {bill.items.map((item, idx) => (
            <div key={idx} style={{ display:'grid', gridTemplateColumns:'1fr 120px 100px 100px 36px', gap:8, marginBottom:10, alignItems:'end' }}>
              <div>
                {idx === 0 && <label className="form-label" style={{ marginBottom:4 }}>Description</label>}
                <input className="form-control" placeholder="Item description"
                  value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)} />
              </div>
              <div>
                {idx === 0 && <label className="form-label" style={{ marginBottom:4 }}>Type</label>}
                <select className="form-control" value={item.itemType}
                  onChange={e => updateItem(idx, 'itemType', e.target.value)}>
                  <option value="stitching">Stitching</option>
                  <option value="product">Product</option>
                </select>
              </div>
              <div>
                {idx === 0 && <label className="form-label" style={{ marginBottom:4 }}>Qty</label>}
                <input type="number" className="form-control" min="1" value={item.quantity}
                  onChange={e => updateItem(idx, 'quantity', e.target.value)} />
              </div>
              <div>
                {idx === 0 && <label className="form-label" style={{ marginBottom:4 }}>Price (SAR)</label>}
                <input type="number" className="form-control" value={item.price}
                  onChange={e => updateItem(idx, 'price', e.target.value)} />
              </div>
              <div style={{ paddingTop: idx === 0 ? 22 : 0 }}>
                <button className="btn btn-outline btn-icon" style={{ color:'#ef4444' }} onClick={() => removeItem(idx)}
                  disabled={bill.items.length === 1}><FiTrash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Totals */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 320px', gap:16 }}>
        <div className="card">
          <div className="card-header"><h3 className="card-title">Notes</h3></div>
          <div className="card-body">
            <textarea className="form-control" rows={3} placeholder="Optional notes..."
              value={bill.notes} onChange={e => setBill({...bill, notes: e.target.value})} />
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              <div style={{ display:'flex', justifyContent:'space-between' }}>
                <span>Subtotal</span><strong>SAR {subtotal.toFixed(2)}</strong>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span>VAT (%)</span>
                <input type="number" style={{ width:70, padding:'4px 8px', border:'1px solid #e2e8f0', borderRadius:6, textAlign:'right' }}
                  value={bill.vatPercent} onChange={e => setBill({...bill, vatPercent: e.target.value})} />
              </div>
              <div style={{ display:'flex', justifyContent:'space-between' }}>
                <span>VAT Amount</span><span>SAR {vatAmt.toFixed(2)}</span>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', borderTop:'2px solid #1e293b', paddingTop:8 }}>
                <strong style={{ fontSize:16 }}>Grand Total</strong>
                <strong style={{ fontSize:16 }}>SAR {total.toFixed(2)}</strong>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span>Advance Paid</span>
                <input type="number" style={{ width:100, padding:'4px 8px', border:'1px solid #e2e8f0', borderRadius:6, textAlign:'right' }}
                  value={bill.advancePaid} onChange={e => setBill({...bill, advancePaid: e.target.value})} />
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', background: remaining > 0 ? '#fee2e2' : '#d1fae5', padding:'8px 12px', borderRadius:6 }}>
                <span style={{ fontWeight:600 }}>Remaining</span>
                <strong style={{ color: remaining > 0 ? '#ef4444' : '#10b981' }}>SAR {remaining.toFixed(2)}</strong>
              </div>
            </div>
            <button className="btn btn-accent btn-block btn-lg" style={{ marginTop:16 }}
              onClick={handleSubmit} disabled={loading}>
              {loading ? 'Creating Bill...' : '✓ Create Bill & Send Email'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}